import {
	BeforeInsert,
	BeforeUpdate,
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { parsePhoneWithExtension } from '../../utils/validation';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';

@Index('client_contact__fk_client__id_fk', ['fkClientId'], {})
// The contact sweep reads changes in `_modified_date` order (PD-17064).
@Index('client_contact__modified_date', ['modifiedDate'], {})
@Index('client_contact__fk_client_address', ['fkClientAddressId'], {})
@Index('client_contact_externalId_uindex', ['deletedUserId', 'externalId'], {
	unique: true,
})
// PD-17414: imports look a record up by externalId alone (upserts sent with
// `deleted: true` drop the _deleted_userId filter), which the composite index
// above cannot serve, so every lookup read the whole table.
@Index('client_contact__externalId', ['externalId'], {})
@Entity('client_contact')
export class ClientContactEntity extends BaseEntity {
	@Column('int', { name: '_fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('varchar', { name: 'phone', nullable: true, length: 255 })
	public phone!: string | null;

	@Column('varchar', { name: 'fax', nullable: true, length: 255 })
	public fax!: string | null;

	@Column('varchar', { name: 'email', nullable: true, length: 255 })
	public email!: string | null;

	@Column('varchar', { name: 'type', nullable: true, length: 255 })
	public type!: string | null;

	@Column('varchar', { name: 'phoneType', nullable: true, length: 255 })
	public phoneType!: string | null;

	@Column('varchar', { name: 'externalId', nullable: true, length: 255 })
	public externalId!: string | null;

	@Column('varchar', { name: 'title', nullable: true, length: 255 })
	public title!: string | null;

	@Column('varchar', { name: 'extension', nullable: true, length: 64 })
	public extension!: string | null;

	@Column('text', { name: 'note', nullable: true })
	public note!: string | null;

	@Column('datetime', { name: 'externalModifiedDatetime', nullable: true })
	public externalModifiedDatetime!: Date | null;

	// Social profile handles/URLs for the contact. Retained after the Recon
	// social-prospecting feature was removed (PD-16042); the columns still exist
	// on every tenant and dropping them would need a destructive migration.
	@Column('varchar', { name: 'twitter_handle', length: 50, nullable: true })
	public twitterHandle!: string | null;

	@Column('varchar', { name: 'twitter_url', length: 255, nullable: true })
	public twitterUrl!: string | null;

	@Column('varchar', { name: 'linkedin_url', length: 255, nullable: true })
	public linkedinUrl!: string | null;

	@Column('varchar', { name: 'youtube_channel_id', length: 50, nullable: true })
	public youtubeChannelId!: string | null;

	@Column('varchar', {
		name: 'youtube_channel_url',
		length: 255,
		nullable: true,
	})
	public youtubeChannelUrl!: string | null;

	@Column('varchar', { name: 'discord_id', length: 50, nullable: true })
	public discordId!: string | null;

	@Column('varchar', { name: 'discord_username', length: 100, nullable: true })
	public discordUsername!: string | null;

	@Column('varchar', { name: 'reddit_username', length: 50, nullable: true })
	public redditUsername!: string | null;

	@Column('varchar', { name: 'bluesky_handle', length: 100, nullable: true })
	public blueskyHandle!: string | null;

	// Contact-level marketing suppression, kept separate from the account-level
	// flags on ClientEntity. do-not-email and do-not-call are independent: a cold
	// email unsubscribe (a STOP/unsubscribe reply with no preference menu) sets
	// isDoNotEmail only. See docs/claude/crm/campaign-system-v1.md.
	@Column({ type: 'boolean', name: 'isDoNotEmail', width: 1, default: () => "'0'" })
	public isDoNotEmail!: boolean;

	@Column({ type: 'boolean', name: 'isDoNotCall', width: 1, default: () => "'0'" })
	public isDoNotCall!: boolean;

	@Column('datetime', { name: 'emailUnsubscribedDate', nullable: true })
	public emailUnsubscribedDate!: Date | null;

	// PD-17053: data the ERP imports already receive and used to drop. All
	// additive (nullable or defaulted), so SM1's inserts are unaffected.

	/** Mobile phone, separate from the direct or office `phone`. */
	@Column('varchar', { name: 'mobilePhone', length: 64, nullable: true })
	public mobilePhone!: string | null;

	/** False when the source marks the contact inactive (e.g. NetSuite isinactive). */
	@Column({ type: 'boolean', name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	/**
	 * The role or contact type exactly as the ERP sends it (Workday contactType,
	 * a NetSuite relationship role). Mapped to the tenant's contact roles by
	 * name; the raw value is kept so an unmapped role is never lost.
	 */
	@Column('varchar', { name: 'erpRole', length: 255, nullable: true })
	public erpRole!: string | null;

	/** The ship-to (job site) the contact belongs to, when the ERP knows it. */
	@Column('int', { name: 'fk_client_address__id', unsigned: true, nullable: true })
	public fkClientAddressId!: number | null;

	@OneToMany(() => ClientEntity, (client) => client.fkClientContact, {})
	public clients!: Array<ClientEntity>;

	@ManyToOne(() => ClientEntity, (client) => client.clientContacts, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	// Keep the contact phone in clean E.164 for click-to-dial and caller-ID /
	// client-vendor mapping: split a trailing extension into the dedicated
	// `extension` column (only when one wasn't set explicitly) and normalize the
	// base number. Mirrors the ClientEntity / VendorEntity hooks. PD-14494.
	@BeforeInsert()
	@BeforeUpdate()
	// `protected` (not `private`): invoked by TypeORM via the decorators, not by
	// direct calls, so TS noUnusedLocals (TS6133) would wrongly flag a `private`
	// version as unused.
	protected _normalizePhone(): void {
		if (this.phone != null && this.phone !== '') {
			const { e164, extension } = parsePhoneWithExtension(this.phone);
			if (
				extension != null &&
				(this.extension == null || this.extension === '')
			) {
				this.extension = extension;
			}
			if (e164 != null) {
				this.phone = e164;
			}
		}
	}
}
